import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import QrScanner from "qr-scanner";
import dayjs from "dayjs";

import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";

import DrawerAppBar from "../components/DrawerAppBar";
import Title from "../components/Title";

import { useJwtStorage, useLocalStorage } from "../hooks/useLocalStorage";
import { isValidUrl } from "../services/StringService";
import { doService } from "../services/ServiceHelper";
import {
  decryptActivitySignup,
  ACTIVITY_QR_KEY,
  decryptActivityAttend,
  doSingleSignup,
  getActivityDateDetails,
  doAttend,
  doSingleSignupAttend,
} from "../services/ActivityService";
import { PASS_QR_KEY, decryptLeavePass, decryptReturnPass, createPass, returnQrPass } from "../services/PassService";
import { getYYYYMMDD } from "../services/DateService";
import { showError, showSuccess } from "../components/ATPDialogs";

const QrScan = () => {
  const [_jwt] = useJwtStorage();
  const [_school] = useLocalStorage("Auth", "school", null);

  const navigate = useNavigate();

  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const stoppedRef = useRef(false);
  const handlingScanRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function startScanner() {
      if (!videoRef.current || scannerRef.current) return;

      const scanner = new QrScanner(
        videoRef.current,
        (result) => handleResult(scanner, result),
        {
          preferredCamera: "environment",
        },
      );

      scannerRef.current = scanner;

      try {
        await scanner.start();
        if (cancelled || stoppedRef.current) {
          scanner.stop();
        }
      } catch (err) {
        // Ignore this specific benign play/load race
        const msg = String(err?.message || err);
        if (
          !msg.includes("play() request was interrupted by a new load request")
        ) {
          console.error(err);
        }
      }
    }

    startScanner();

    return () => {
      cancelled = true;
      stoppedRef.current = true;

      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, []);

  const handleResult = (scanner, result) => {
    const text = result?.data || "";

    if (handlingScanRef.current) return;

    if (!isValidUrl(text)) {
      console.log("Scanned text is not a valid URL:", text);
      return;
    }

    handlingScanRef.current = true;
    stoppedRef.current = true;

    // Stop immediately once URL is found
    scanner.stop();

    const path = getPathFromUrl(text);

    if (path.startsWith("/student/ad/signup/")) {
      signup(path);
    } else if (path.startsWith("/student/ad/attend/")) {
      attend(path);
    } else if (path.startsWith("/student/ad/signup-attend/")) {
      signupAttend(path);
    } else if (path.startsWith("/student/pass/qr/leave/")) {
      passLeave(path);
    } else if (path.startsWith("/student/pass/qr/return/")) {
      passReturn(path);
    }

    console.log("URL detected:", text);
  };

  const passReturn = (path) => {
    const split = path.split("/");
    const qrCode = split[split.length - 1];
    const qrDetails = decryptReturnPass(PASS_QR_KEY, qrCode);

    doService(
      returnQrPass(_school, _jwt, qrDetails.userEid, getYYYYMMDD(), "Y"),
      (results) => {
        showSuccess("Pass Returned", "Your pass has been RETURNED.", () => {
          navigate("/student/welcome");
        });
        return;
      },
      (detail) => {
        showError("Unable to Return Pass", detail, () => {
          navigate("/student/welcome");
        });
        return;
      }
    );  };

  const passLeave = (path) => {
    const split = path.split("/");
    const qrCode = split[split.length - 1];
    const qrDetails = decryptLeavePass(PASS_QR_KEY, qrCode);

    doService(
      createPass(
        _school,
        _jwt,
        qrDetails.passTypeEid,
        dayjs().format("YYYY-MM-DD"),
        qrDetails.userEid,
        qrDetails.locationEid,
        null,
        null,
        null,
        null,
        "Y"
      ),
      (results) => {
        const thePass = results.data[0].pass;
        // const thePoint = results.data.points[0];
        // console.log(results.data);
        const theStudent = results.data[0].student;

        let message = `A pass has been created AND APPROVED for ${
          theStudent.first_name
        } ${theStudent.last_name} on ${dayjs(thePass.dateCreated).format(
          "MM/DD hh:mm a"
        )} (${thePass.typeName}).`;

        showSuccess("Pass Created", message, () => {
          navigate("/student/welcome");
        });
        return;
      },
      (detail) => {
        showError("Unable to Create Pass", detail, () => {
          navigate("/student/welcome");
        });
        return;
      }
    );
    // http://localhost:3003/#/student/pass/qr/leave/eyJpdiI6IlNxMWpyaHltcVhkeFR5SFd0VkZOTlE9PSIsInYiOjEsIml0ZXIiOjEwMDAwLCJrcyI6MTI4LCJ0cyI6NjQsIm1vZGUiOiJjY20iLCJhZGF0YSI6IiIsImNpcGhlciI6ImFlcyIsInNhbHQiOiIzdURPRCt3L0wxWT0iLCJjdCI6IjR5RURKNEFqWmJ6aTVLT1N2U0ZzOGY2cUJ4RmxRRnphd1EvSWZRMHFEbXRtQlpsakczWHc0U2xFcVN5bzRvZHEzMlNzWFpqTXQxWXlxakJGZStQem9JVVBMbFByUGIzZHBFNmdyQUwrdkpsbVNlRCtaa0QyQmdGakxrN1FnZExxYjl1RnZiTUd0ZzVlY3JJUTlaVXR5WmY3U0hzTDhnPT0ifQ==

  }

  const signup = (path) => {
    const split = path.split("/");
    const qrCode = split[split.length - 1];
    const activityDateEid = decryptActivitySignup(ACTIVITY_QR_KEY, qrCode);

    doSingleSignup(_school, _jwt, activityDateEid).then((results) => {
      if (results.detail != null) {
        showError("Unable to Add Activity", results.detail);
        return;
      }

      const lastName = results.data.last_name;
      const firstName = results.data.first_name;

      showSuccess(
        "Activity Added!",
        `${lastName}, ${firstName} has been added to the activity.`,
        () => {
          navigate("/student/welcome");
        },
      );
    });
  };

  const attend = (path) => {
      const split = path.split("/");
      const qrCode = split[split.length - 1];
      const activityDateEid = decryptActivityAttend(ACTIVITY_QR_KEY, qrCode);

      doService(
        getActivityDateDetails(_school, _jwt, activityDateEid),
        (results) => {
          // sign up the student
          const activityDate = results.data;

          if (activityDate.period == null) {
            showError(
              "Unable to Take Attendance",
              "Activity has been deleted.",
            );
            return;
          }

          // try to mark as attended
          doAttend(_school, _jwt, activityDateEid).then((results) => {
            if (results.detail != null) {
              showError("Unable to Take Attendance", results.detail);
              return;
            }

            const lastName = results.data.last_name;
            const firstName = results.data.first_name;
            const periodLabel = results.data.period_label;
            const activityName = results.data.activity_name;
            const currentDate = results.data.current_date_time;

            showSuccess(
              "Attendance Taken!",
              `${lastName}, ${firstName} has been marked as PRESENT for ${periodLabel}'s ${activityName}, on ${currentDate}.`,
              () => {
                navigate("/student/welcome");
              },
            );
          });
        },
        (error) => {
          showError("Unable to Use Activity QR", error, () => {
            navigate("/student/welcome");
          });
          return;
        },
      );

  };

  const signupAttend = (path) => {
      const split = path.split("/");
      const qrCode = split[split.length - 1];
      const activityDateEid = decryptActivityAttend(ACTIVITY_QR_KEY, qrCode);
      doService(
        getActivityDateDetails(_school, _jwt, activityDateEid),
        (results) => {
          // sign up the student
          const activityDate = results.data;

          if (activityDate.period == null) {
            showError("Unable to Add Activity", "Activity has been deleted.");
            return;
          }

          // we ignore if maxxed out

          // try to sign up
          doSingleSignupAttend(_school, _jwt, activityDateEid).then(
            (results) => {
              if (results.detail != null) {
                showError("Unable to Add Activity", results.detail);
                return;
              }

              const lastName = results.data.last_name;
              const firstName = results.data.first_name;

              showSuccess(
                "Activity Added!",
                `${lastName}, ${firstName} has been added to the activity and marked as present.`,
                () => {
                  navigate("/student/welcome");
                },
              );
            },
          );
        },
        (error) => {
          showError("Unable to Use Activity QR", error, () => {
            navigate("/student/welcome");
          });
          return;
        },
      );

  };

  const getPathFromUrl = (url) => {
    try {
      const u = new URL(url);

      // If using hash routing (like your example)
      if (u.hash && u.hash.startsWith("#/")) {
        return u.hash.substring(1); // remove leading "#"
      }

      // Otherwise return normal pathname
      return u.pathname;
    } catch (e) {
      return null;
    }
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Scan QR Code" showBack={true} />

        <Alert severity="info" sx={{ mb: 0.5, pt: 0, pb: 0 }}>
          Use this page to Scan AllTimely QR Codes.
          <br />
          NOTE: You may need to click "Allow" to permit the browser to use the
          camera.
          <br />
          NOTE: If the camera does not appear, try refreshing the page and
          ensuring you allow camera permissions when prompted.
        </Alert>
        <br />

        <video ref={videoRef} style={{ width: "100%" }} />
      </Container>
    </>
  );
};

export default QrScan;
